import React, { memo, useMemo } from "react"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"
import {
    Card,
    CardBody,
    Col,
} from "reactstrap"
import './basic.css';

/**
 * Builds store query URL based on title
 */
const getStoreQueryUrl = (title) => {
    const titleMap = {
        "Headless Stores": "headless",
        "Basic Stores": "basic",
        "Shopify Plus Stores": "shopify-plus"
    };
    return titleMap[title] || title.split(" ")[0]?.toLowerCase();
};

/**
 * Renders edit link based on linkUrl type
 */
const renderEditLink = ({ edit, linkUrl, store_domain, data, data2, store_details, store_status, field }) => {
    if (!edit && data2 === '-1') return null;

    const editConfigs = {
        'charge-edit': {
            pathname: "/usage-charges-edit",
            state: { data: { store_domain, variable_charge: data, fixed_charge: data2, store_details, store_status } }
        },
        'campaign-charge-edit': {
            pathname: "/campaign-charge-edit",
            state: { data: { store_domain, campaign_variable_charge: data, campaign_fixed_charge: data2, store_details, store_status } }
        },
        default: {
            pathname: `/${linkUrl}`,
            state: { data: { store_domain, value: data, store_details, store_status, field } }
        }
    };

    const config = editConfigs[linkUrl] || editConfigs.default;

    // Build search params - include field for placement-edit and similar pages
    const searchParams = new URLSearchParams();
    if (store_domain) {
        searchParams.set('store', store_domain);
    }
    // Include field in query string for placement-edit and similar pages to ensure it persists
    if (field && (linkUrl === 'placement-edit' || linkUrl === 'price-selector-edit')) {
        searchParams.set('field', field);
    }
    const search = searchParams.toString() ? `?${searchParams.toString()}` : undefined;

    return (
        <Link to={{ ...config, search }}>
            <i className="fa fa-edit"></i> Edit
        </Link>
    );
};

/**
 * Renders the data value based on props
 */
const renderDataValue = ({ linkUrl, data, data2, store_domain, secondParams, title, price }) => {
    // Link with data2
    if (linkUrl && data2 !== '-1') {
        return <h5 className="mb-3">{data}% + ${data2}</h5>;
    }

    // Simple link
    if (linkUrl && data2 === '-1') {
        return (
            <Link to={{ pathname: `/${linkUrl}`, search: store_domain ? `?store=${encodeURIComponent(store_domain)}` : undefined, state: { data: { store_domain } } }}>
                <h5 className="mb-3">{data || "---"}</h5>
            </Link>
        );
    }

    // No link - render based on secondParams
    if (secondParams !== "yes") {
        return <h5 className="mb-3" style={{ display: "flex", justifyContent: "space-between" }}><span>{data}</span></h5>;
    }

    // With secondParams - render link and optional price
    const queryUrl = getStoreQueryUrl(title);
    return (
        <h5 className="mb-3" style={{ display: "flex", justifyContent: "space-between" }}>
            <Link to={`/stores/?q=${queryUrl}`}>{data}</Link>
            {title !== "Headless Stores" && title !== "Basic Stores" && title !== "Shopify Plus Stores" && (
                <span>MRR: ${price}</span>
            )}
        </h5>
    );
};

const BasicCountInfo = ({
    data = '',
    data2 = '-1',
    title = '',
    edit = false,
    field = "",
    colSize = 3,
    store_domain = '',
    linkUrl = '',
    store_details = {},
    store_status = '',
    price = 0,
    secondParams = '',
    isClick = false
}) => {
    const editLink = useMemo(() => {
        if (!edit && data2 === '-1') return null;
        if (edit) {
            return renderEditLink({ edit, linkUrl, store_domain, data, store_details, store_status, field });
        }
        return (data2 !== '-1' && (linkUrl === 'charge-edit' || linkUrl === 'campaign-charge-edit'))
            ? renderEditLink({ linkUrl, store_domain, data, data2, store_details, store_status })
            : null;
    }, [edit, linkUrl, store_domain, data, data2, store_details, store_status, field]);

    const dataValue = useMemo(() => renderDataValue({ linkUrl, data, data2, store_domain, secondParams, title, price }),
        [linkUrl, data, data2, store_domain, secondParams, title, price]);

    return (
        <Col xl={colSize}>
            <Card>
                <CardBody>
                    <div className="d-flex flex-wrap d-between-flex">
                        <h3 className="card-title mb-3 me-2">{title}</h3>
                        {editLink}
                    </div>
                    <div>
                        {dataValue}
                    </div>
                </CardBody>
            </Card>
        </Col>
    );
};

BasicCountInfo.propTypes = {
    data: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data2: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    edit: PropTypes.bool,
    field: PropTypes.string,
    colSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    store_domain: PropTypes.string,
    linkUrl: PropTypes.string,
    store_details: PropTypes.object,
    store_status: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    secondParams: PropTypes.string,
    isClick: PropTypes.bool,
}

export default memo(BasicCountInfo)
